<template>
  <v-dialog v-model="dialogOpen" :max-width="activeTab === 'excel' ? '700' : '520'" >
    <div class="inout-auto-modal" :class="{ 'excel-mode': activeTab === 'excel' }">
      <!-- 모달 헤더 -->
      <div class="modal-header auto">
        <div class="header-icon">
          <v-icon size="24" color="white">mdi-robot</v-icon>
        </div>
        <div class="header-text">
          <h3>{{ getHeaderTitle }}</h3>
          <p>{{ getHeaderSubtitle }}</p>
        </div>
        <button class="close-btn" @click="closeDialog" :disabled="isProcessing && !processComplete">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <!-- 탭 네비게이션 -->
      <div class="modal-tabs">
        <button 
          type="button" 
          class="modal-tab" 
          :class="{ active: activeTab === 'input' }"
          :disabled="isProcessing && !processComplete"
          @click="activeTab = 'input'"
        >
          <v-icon size="16">mdi-plus-circle</v-icon>
          항목 추가
        </button>
        <button 
          type="button" 
          class="modal-tab excel" 
          :class="{ active: activeTab === 'excel' }"
          :disabled="isProcessing && !processComplete"
          @click="activeTab = 'excel'"
        >
          <v-icon size="16">mdi-file-excel</v-icon>
          엑셀 업로드
        </button>
      </div>

      <!-- 자동 추가 탭 -->
      <div v-show="activeTab === 'input'" class="modal-body">
        <!-- 안내 문구 -->
        <div class="auto-notice">
          <v-icon size="16" color="#f59e0b">mdi-lightbulb-on</v-icon>
          <span>수입/지출, 출납일자, 금액, 적요만 입력하면 나머지는 자동으로 추천됩니다.</span>
        </div>

        <v-form ref="formRef">
          <!-- 1행: 구분 선택 (탭 스타일) -->
          <div class="form-section">
            <div class="gubun-tabs">
              <button 
                type="button"
                class="gubun-tab income" 
                :class="{ active: formData.gubun === 'IN' }"
                @click="formData.gubun = 'IN'"
              >
                <v-icon size="16">mdi-plus-circle</v-icon>
                수입
              </button>
              <button 
                type="button"
                class="gubun-tab expense" 
                :class="{ active: formData.gubun === 'OUT' }"
                @click="formData.gubun = 'OUT'"
              >
                <v-icon size="16">mdi-minus-circle</v-icon>
                지출
              </button>
            </div>
          </div>

          <!-- 2행: 날짜 / 금액 -->
          <div class="form-row two-cols compact">
            <div class="form-group">
              <label class="form-label required">출납일자</label>
              <input
                type="date"
                class="form-input"
                v-model="formData.inout_date"
              />
            </div>
            <div class="form-group">
              <label class="form-label required">금액</label>
              <div class="price-input-box" :class="{ focus: priceFocused }">
                <input
                  type="text"
                  class="price-field"
                  :value="formattedPrice"
                  @input="handlePriceInput"
                  @keydown="handlePriceKeydown"
                  @focus="priceFocused = true"
                  @blur="priceFocused = false"
                  placeholder="0"
                />
                <span class="price-unit">원</span>
              </div>
            </div>
          </div>

          <!-- 3행: 적요 -->
          <div class="form-group">
            <label class="form-label required">적요</label>
            <input
              type="text"
              class="form-input"
              v-model="formData.memo"
              placeholder="적요를 입력하세요 (예: 직원음료, 본인부담금 등)"
              @keyup.enter="predictFields"
            />
          </div>

          <!-- 추천 버튼 -->
          <div class="action-row">
            <button 
              type="button" 
              class="btn-predict"
              :class="{ loading: isPredicting }"
              @click="predictFields"
              :disabled="!canPredict || isPredicting"
            >
              <v-progress-circular v-if="isPredicting" indeterminate size="18" width="2" color="white" />
              <template v-else>
                <v-icon size="18">mdi-auto-fix</v-icon>
                AI 추천 받기
              </template>
            </button>
          </div>

          <!-- 추천 결과 영역 -->
          <div v-if="prediction" class="prediction-result" :class="{ 'high-confidence': prediction.confidence >= 0.8 }">
            <div class="prediction-header">
              <v-icon size="16" :color="prediction.confidence >= 0.8 ? '#10b981' : '#f59e0b'">
                {{ prediction.confidence >= 0.8 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              <span>AI 추천 결과</span>
              <span v-if="prediction.matchCount > 0" class="match-count-badge">
                {{ prediction.matchCount }}건 매칭
              </span>
              <span v-else-if="prediction.ruleKeyword" class="match-count-badge rule">
                "{{ prediction.ruleKeyword }}" 규칙
              </span>
              <span class="confidence-badge" :class="getConfidenceClass(prediction.confidence)">
                신뢰도 {{ Math.round(prediction.confidence * 100) }}%
              </span>
            </div>
            <div class="prediction-items">
              <div class="prediction-item">
                <span class="prediction-label">계정명</span>
                <span class="prediction-value">
                  {{ prediction.scode_name || prediction.scode || '-' }}
                  <span v-if="prediction.scode && prediction.scode_name" class="code-hint">({{ prediction.scode }})</span>
                </span>
              </div>
              <div class="prediction-item">
                <span class="prediction-label">자금원천</span>
                <span class="prediction-value">{{ getFundingName(prediction.funding) }}</span>
              </div>
              <div v-if="formData.gubun === 'OUT'" class="prediction-item rel-acc-item">
                <span class="prediction-label">상대계정</span>
                <span class="prediction-value">
                  {{ prediction.rel_acc_name || prediction.rel_acc || '-' }}
                  <span v-if="prediction.rel_acc && prediction.rel_acc_name" class="code-hint">({{ prediction.rel_acc }})</span>
                  <span v-if="prediction.rel_acc_balance !== undefined" class="balance-info" :class="{ sufficient: prediction.rel_acc_balance >= formData.price, insufficient: prediction.rel_acc_balance < formData.price }">
                    잔액 {{ formatNumber(prediction.rel_acc_balance) }}원
                  </span>
                </span>
              </div>
              <!-- 상대계정 없음 경고 -->
              <div v-if="formData.gubun === 'OUT' && !prediction.rel_acc && prediction.availableRelAccounts && prediction.availableRelAccounts.length === 0" class="no-rel-acc-warning">
                <v-icon size="14" color="#ef4444">mdi-alert</v-icon>
                <span>해당 월에 등록된 수입 항목이 없습니다. 먼저 수입을 등록해주세요.</span>
              </div>
              <!-- 잔액 부족 경고 -->
              <div v-else-if="formData.gubun === 'OUT' && prediction.rel_acc_balance !== undefined && prediction.rel_acc_balance < formData.price" class="balance-warning">
                <v-icon size="14" color="#f59e0b">mdi-alert-circle</v-icon>
                <span>상대계정 잔액이 부족합니다. 다른 상대계정을 선택하세요.</span>
              </div>
              <div class="prediction-item source">
                <span class="prediction-label">분석 방식</span>
                <span class="prediction-value source-tag" :class="prediction.matchType || prediction.source">
                  {{ prediction.matchDescription || getSourceName(prediction.source) }}
                </span>
              </div>
            </div>

            <!-- 상대계정 선택 (지출이고 여러 개 있을 때) -->
            <div v-if="formData.gubun === 'OUT' && prediction.availableRelAccounts && prediction.availableRelAccounts.length > 1" class="rel-acc-selection">
              <div class="rel-acc-header">
                <v-icon size="14" color="#6366f1">mdi-swap-horizontal</v-icon>
                <span>상대계정 선택</span>
              </div>
              <div class="rel-acc-list">
                <div 
                  v-for="(relAcc, idx) in prediction.availableRelAccounts" 
                  :key="idx"
                  class="rel-acc-option"
                  :class="{ 
                    active: prediction.rel_acc === relAcc.scode,
                    disabled: relAcc.availableBalance < formData.price
                  }"
                  @click="selectRelAcc(relAcc)"
                >
                  <div class="rel-acc-info">
                    <span class="rel-acc-name">{{ relAcc.sname }}</span>
                    <span class="rel-acc-code">({{ relAcc.scode }})</span>
                  </div>
                  <span class="rel-acc-balance" :class="{ insufficient: relAcc.availableBalance < formData.price }">
                    {{ formatNumber(relAcc.availableBalance) }}원
                  </span>
                </div>
              </div>
            </div>

            <!-- 대안 추천이 있는 경우 -->
            <div v-if="prediction.alternatives && prediction.alternatives.length > 0" class="alternatives">
              <div class="alternatives-header">다른 추천</div>
              <div 
                v-for="(alt, idx) in prediction.alternatives" 
                :key="idx"
                class="alternative-item"
                @click="selectAlternative(alt)"
              >
                <div class="alt-info">
                  <span class="alt-scode">{{ alt.scode_name || alt.scode }}</span>
                  <span v-if="alt.rel_acc" class="alt-rel-acc">
                    → {{ alt.rel_acc_name || alt.rel_acc }}
                  </span>
                </div>
                <span class="alt-count">{{ alt.matchCount }}건</span>
              </div>
            </div>

            <!-- 적요 변경 추천 -->
            <div v-if="prediction.suggestedMemos && prediction.suggestedMemos.length > 0" class="suggested-memos">
              <div class="suggested-header">
                <v-icon size="14" color="#6366f1">mdi-lightbulb-outline</v-icon>
                <span>적요 변경 추천</span>
              </div>
              <div 
                v-for="(sm, idx) in prediction.suggestedMemos" 
                :key="idx"
                class="suggested-item"
                @click="applyMemo(sm.memo)"
              >
                <span class="suggested-memo">{{ sm.memo }}</span>
                <span class="suggested-count">{{ sm.usageCount }}회 사용</span>
              </div>
            </div>
          </div>

          <!-- 예측 실패 -->
          <div v-if="predictionError" class="prediction-error">
            <v-icon size="16" color="#ef4444">mdi-alert-circle</v-icon>
            <span>{{ predictionError }}</span>
          </div>
        </v-form>
      </div>

      <!-- 엑셀 업로드 탭 -->
      <div v-show="activeTab === 'excel'" class="modal-body excel-body">
        <div class="excel-upload-section">
          <!-- 파일 업로드 영역 -->
          <div 
            class="upload-dropzone"
            :class="{ 'drag-over': isDragOver, 'has-file': excelFile }"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input 
              ref="fileInputRef"
              type="file" 
              accept=".xlsx,.xls"
              @change="handleFileSelect"
              hidden
            />
            <template v-if="!excelFile">
              <v-icon size="48" color="#94a3b8">mdi-file-excel-outline</v-icon>
              <p class="upload-text">엑셀 파일을 드래그하거나 클릭하여 업로드</p>
              <p class="upload-hint">.xlsx, .xls 파일만 지원</p>
            </template>
            <template v-else>
              <v-icon size="40" color="#10b981">mdi-file-excel</v-icon>
              <p class="file-name">{{ excelFile.name }}</p>
              <p class="file-size">{{ formatFileSize(excelFile.size) }}</p>
              <button type="button" class="btn-remove-file" @click.stop="removeFile">
                <v-icon size="16">mdi-close</v-icon>
              </button>
            </template>
          </div>

          <!-- 분석 버튼 -->
          <button 
            type="button" 
            class="btn-analyze"
            :class="{ loading: isAnalyzing }"
            @click="analyzeExcel"
            :disabled="!excelFile || isAnalyzing"
          >
            <v-progress-circular v-if="isAnalyzing" indeterminate size="18" width="2" color="white" />
            <template v-else>
              <v-icon size="18">mdi-table-search</v-icon>
              헤더 분석하기
            </template>
          </button>

          <!-- 분석 결과 (축약) -->
          <div v-if="excelAnalysis" class="analysis-result">
            <div class="analysis-summary">
              <v-icon size="18" color="#10b981">mdi-check-circle</v-icon>
              <div class="summary-text">
                <span class="summary-title">분석 완료</span>
                <span class="summary-detail">
                  {{ excelAnalysis.sheetCount }}개 시트 · 
                  {{ getTotalMappedColumns() }}개 컬럼 매핑
                </span>
              </div>
            </div>
          </div>

          <!-- 데이터 등록 버튼 -->
          <button 
            v-if="excelAnalysis && !isProcessing && !processComplete"
            type="button" 
            class="btn-process"
            @click="processExcelData"
          >
            <v-icon size="18">mdi-database-import</v-icon>
            AI 분석 후 데이터 등록하기
          </button>

          <!-- 진행 상황 표시 -->
          <div v-if="isProcessing || processComplete" class="process-status">
            <div class="process-header">
              <v-icon size="18" :color="processComplete ? '#10b981' : '#6366f1'">
                {{ processComplete ? 'mdi-check-circle' : 'mdi-loading mdi-spin' }}
              </v-icon>
              <span>{{ processStatus.message }}</span>
            </div>

            <!-- 진행바 -->
            <div v-if="(processStatus.total ?? 0) > 0" class="process-progress">
              <div class="progress-info">
                <span class="phase-name">
                  {{ processStatus.phase === 'income' ? '수입 처리' : processStatus.phase === 'expense' ? '지출 처리' : '데이터 처리' }}
                </span>
                <span class="progress-count">
                  {{ processStatus.current || 0 }} / {{ processStatus.total || 0 }}
                  ({{ Math.round(((processStatus.current || 0) / (processStatus.total || 1)) * 100) }}%)
                </span>
              </div>
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :class="processStatus.phase || 'income'"
                  :style="{ width: `${((processStatus.current || 0) / (processStatus.total || 1)) * 100}%` }"
                ></div>
              </div>
            </div>
            
            <!-- 처리 중인 내역 간단 표시 -->
            <div v-if="processStatus.incomeProcessed !== undefined || processStatus.expenseProcessed !== undefined" class="process-counts">
              <span class="income-count">수입: {{ processStatus.incomeProcessed || 0 }}건</span>
              <span class="expense-count">지출: {{ processStatus.expenseProcessed || 0 }}건</span>
            </div>

            <!-- 현재 처리 중인 항목 -->
            <div v-if="processStatus.currentItem" class="current-item">
              <div class="item-info">
                <span class="sheet-badge">{{ processStatus.currentItem.sheetName }}</span>
                <span class="row-badge">{{ processStatus.currentItem.rowIndex }}행</span>
              </div>
              <div class="item-detail">
                <span class="memo">{{ processStatus.currentItem.memo }}</span>
                <span v-if="processStatus.currentItem.scode_name" class="prediction">
                  → {{ processStatus.currentItem.scode_name }}
                  <span v-if="processStatus.currentItem.rel_acc_name"> / {{ processStatus.currentItem.rel_acc_name }}</span>
                </span>
              </div>
            </div>

            <!-- 처리 로그 -->
            <div class="process-log" ref="processLogRef">
              <div 
                v-for="(log, idx) in processLogs.slice(-20)" 
                :key="idx"
                class="log-item"
                :class="log.type"
              >
                <v-icon size="12" :color="getLogIconColor(log.type)">{{ getLogIcon(log.type) }}</v-icon>
                <span>{{ log.message }}</span>
              </div>
            </div>

            <!-- 완료 결과 -->
            <div v-if="processComplete" class="process-result">
              <div class="result-stats">
                <div class="stat income">
                  <v-icon size="16" color="#10b981">mdi-plus-circle</v-icon>
                  <span>수입 {{ processResult.incomeCount }}건</span>
                </div>
                <div class="stat expense">
                  <v-icon size="16" color="#ef4444">mdi-minus-circle</v-icon>
                  <span>지출 {{ processResult.expenseCount }}건</span>
                </div>
                <div v-if="processResult.errorCount > 0" class="stat error">
                  <v-icon size="16" color="#f59e0b">mdi-alert</v-icon>
                  <span>오류 {{ processResult.errorCount }}건</span>
                </div>
              </div>
              <button type="button" class="btn-done" @click="onProcessComplete">
                <v-icon size="16">mdi-check</v-icon>
                완료
              </button>
            </div>
          </div>

          <!-- 분석 오류 -->
          <div v-if="excelError" class="analysis-error">
            <v-icon size="16" color="#ef4444">mdi-alert-circle</v-icon>
            <span>{{ excelError }}</span>
          </div>
        </div>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <button type="button" class="btn-cancel" @click="closeDialog">닫기</button>
        <button 
          v-if="activeTab === 'input'"
          type="button" 
          class="btn-submit" 
          :class="{ disabled: !canSave, loading: isSaving }"
          @click="saveInout"
          :disabled="!canSave || isSaving"
        >
          <v-progress-circular v-if="isSaving" indeterminate size="18" width="2" color="white" />
          <span v-else>저장하기</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const store = useStore();

// Props
const props = defineProps<{
  modelValue: boolean;
  currentYear: string;
  currentMonth: string;
}>();

// Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved'): void;
}>();

// 자금원 코드 매핑
const FUNDING_MAP: Record<string, string> = {
  '04': '자부담',
  '05': '후원금',
  '06': '수익사업',
  '07': '보조금'
};

// 상태
const dialogOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const activeTab = ref<'input' | 'excel'>('input');
const formRef = ref();
const isPredicting = ref(false);
const isSaving = ref(false);
const priceFocused = ref(false);
const predictionError = ref('');

// 엑셀 업로드 상태
const fileInputRef = ref<HTMLInputElement | null>(null);
const excelFile = ref<File | null>(null);
const isDragOver = ref(false);
const isAnalyzing = ref(false);
const excelAnalysis = ref<any>(null);
const excelError = ref('');

// 엑셀 데이터 처리 상태
const isProcessing = ref(false);
const processComplete = ref(false);
const processStatus = ref<{
  message: string;
  phase?: 'income' | 'expense' | string;
  current?: number;
  total?: number;
  incomeProcessed?: number;
  expenseProcessed?: number;
  currentItem?: {
    sheetName: string;
    rowIndex: number;
    memo: string;
    inout_date?: string;
    scode_name?: string;
    rel_acc_name?: string;
  };
}>({ message: '' });
const processLogs = ref<Array<{ type: string; message: string }>>([]);
const processResult = ref<{ incomeCount: number; expenseCount: number; errorCount: number }>({ incomeCount: 0, expenseCount: 0, errorCount: 0 });
const processLogRef = ref<HTMLElement | null>(null);

interface FormData {
  gubun: 'IN' | 'OUT';
  inout_date: string;
  price: number;
  memo: string;
}

interface RelAccOption {
  scode: string;
  sname: string;
  totalIncome: number;
  usedAmount: number;
  availableBalance: number;
  isAffordable: boolean;
}

interface Prediction {
  scode: string | null;
  scode_name: string | null;
  funding: string | null;
  rel_acc: string | null;
  rel_acc_name: string | null;
  rel_acc_balance?: number;
  confidence: number;
  source: 'rule' | 'bigdata' | 'mixed' | null;
  matchCount: number;
  ruleKeyword: string | null;
  matchType: string | null;
  matchDescription: string | null;
  alternatives: Array<{
    scode: string;
    scode_name: string | null;
    funding: string;
    rel_acc: string;
    rel_acc_name: string | null;
    matchCount: number;
    keywordScore?: number;
  }>;
  suggestedMemos?: Array<{
    memo: string;
    usageCount: number;
  }>;
  availableRelAccounts?: RelAccOption[];
}

const formData = ref<FormData>({
  gubun: 'OUT',
  inout_date: '',
  price: 0,
  memo: ''
});

const prediction = ref<Prediction | null>(null);

// 금액 포맷팅
const formattedPrice = computed(() => {
  return formData.value.price ? formData.value.price.toLocaleString() : '';
});

// 예측 가능 여부
const canPredict = computed(() => {
  return formData.value.gubun && 
         formData.value.memo?.trim();
});

// 저장 가능 여부
const canSave = computed(() => {
  return formData.value.gubun &&
         formData.value.inout_date &&
         formData.value.price > 0 &&
         formData.value.memo?.trim() &&
         prediction.value?.scode;
});

// 헤더 타이틀
const getHeaderTitle = computed(() => {
  if (activeTab.value === 'excel') return '엑셀 업로드';
  return '자동 항목 추가';
});

const getHeaderSubtitle = computed(() => {
  if (activeTab.value === 'excel') return '엑셀 파일을 업로드하여 항목을 일괄 추가합니다';
  return 'AI가 계정코드, 자금원천, 상대계정을 추천합니다';
});

// 자금원천 이름 가져오기
const getFundingName = (code: string | null | undefined): string => {
  if (!code) return '-';
  return FUNDING_MAP[code] || code;
};

// 숫자 포맷팅
const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('ko-KR').format(num);
};

// 추천 방식 이름
const getSourceName = (source: string | null): string => {
  if (!source) return '-';
  const names: Record<string, string> = {
    'rule': '규칙 기반',
    'bigdata': '빅데이터',
    'mixed': '복합 추천'
  };
  return names[source] || source;
};

// 신뢰도 클래스
const getConfidenceClass = (confidence: number): string => {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
};

// 매핑된 컬럼 수 계산
const getTotalMappedColumns = (): number => {
  if (!excelAnalysis.value?.sheets) return 0;
  let count = 0;
  for (const sheet of excelAnalysis.value.sheets) {
    if (sheet.detectedHeader?.columnMapping) {
      count += sheet.detectedHeader.columnMapping.filter((m: any) => m.field).length;
    }
  }
  return count;
};

// 금액 입력 핸들러
const handlePriceInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const rawValue = target.value.replace(/[^0-9]/g, '');
  const numericValue = rawValue ? parseInt(rawValue, 10) : 0;
  formData.value.price = numericValue;
  target.value = numericValue ? numericValue.toLocaleString() : '';
};

const handlePriceKeydown = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  const isNumber = /^[0-9]$/.test(event.key);
  if (!isNumber && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

// AI 예측 요청
const predictFields = async () => {
  if (!canPredict.value) return;
  
  isPredicting.value = true;
  prediction.value = null;
  predictionError.value = '';
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    const response = await axios.post('/api/fna/inout/predict', {
      gubun: formData.value.gubun,
      memo: formData.value.memo,
      price: formData.value.price,
      // 지출일 때 상대계정 추천을 위해 추가 정보 전달
      sisulCode,
      year: props.currentYear,
      month: props.currentMonth
    });
    
    if (response.data.success) {
      prediction.value = response.data.data;
      
      // 예측 결과가 없는 경우
      if (!prediction.value?.scode) {
        predictionError.value = '유사한 패턴을 찾을 수 없습니다. 적요를 다르게 입력해보세요.';
      }
    } else {
      predictionError.value = response.data.message || '예측에 실패했습니다.';
    }
  } catch (error: any) {
    console.error('예측 오류:', error);
    predictionError.value = error.response?.data?.message || '예측 중 오류가 발생했습니다.';
  } finally {
    isPredicting.value = false;
  }
};

// 대안 선택
const selectAlternative = (alt: { scode: string; scode_name: string | null; funding: string; rel_acc: string; rel_acc_name: string | null }) => {
  if (prediction.value) {
    prediction.value.scode = alt.scode;
    prediction.value.scode_name = alt.scode_name;
    prediction.value.funding = alt.funding;
    prediction.value.rel_acc = alt.rel_acc;
    prediction.value.rel_acc_name = alt.rel_acc_name;
  }
};

// 상대계정 선택
const selectRelAcc = (relAcc: { scode: string; sname: string; availableBalance: number }) => {
  if (prediction.value) {
    prediction.value.rel_acc = relAcc.scode;
    prediction.value.rel_acc_name = relAcc.sname;
    prediction.value.rel_acc_balance = relAcc.availableBalance;
  }
};

// 추천 적요 적용
const applyMemo = (memo: string) => {
  formData.value.memo = memo;
  // 다시 예측 실행
  predictFields();
};

// 저장
const saveInout = async () => {
  if (!canSave.value || !prediction.value) return;
  
  isSaving.value = true;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    const payload = {
      m_sisul_code: sisulCode,
      hi_gubun: formData.value.gubun,
      hi_scode: prediction.value.scode,
      hi_memo: formData.value.memo,
      hi_price: formData.value.price,
      hi_inout_date: formData.value.inout_date,
      hi_funding: prediction.value.funding,
      hi_rel_acc: prediction.value.rel_acc
    };
    
    await axios.post('/api/fna/inout', payload);
    
    emit('saved');
    closeDialog();
  } catch (error: any) {
    console.error('저장 오류:', error);
    predictionError.value = error.response?.data?.message || '저장 중 오류가 발생했습니다.';
  } finally {
    isSaving.value = false;
  }
};

// ============================================
// 엑셀 업로드 관련 함수
// ============================================

// 파일 입력 트리거
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// 파일 선택 핸들러
const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    setExcelFile(file);
  }
};

// 드래그 앤 드롭 핸들러
const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext === '.xlsx' || ext === '.xls') {
      setExcelFile(file);
    } else {
      excelError.value = '엑셀 파일(xls, xlsx)만 업로드 가능합니다.';
    }
  }
};

// 파일 설정
const setExcelFile = (file: File) => {
  excelFile.value = file;
  excelAnalysis.value = null;
  excelError.value = '';
  // 이전 처리 상태 초기화
  processComplete.value = false;
  processStatus.value = { message: '' };
  processLogs.value = [];
  processResult.value = { incomeCount: 0, expenseCount: 0, errorCount: 0 };
};

// 파일 제거
const removeFile = () => {
  excelFile.value = null;
  excelAnalysis.value = null;
  excelError.value = '';
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// 파일 크기 포맷
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// 엑셀 분석
const analyzeExcel = async () => {
  if (!excelFile.value) return;
  
  isAnalyzing.value = true;
  excelAnalysis.value = null;
  excelError.value = '';
  
  try {
    const formData = new FormData();
    formData.append('file', excelFile.value);
    
    const response = await axios.post('/api/fna/inout/excel/detect-headers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.success) {
      excelAnalysis.value = response.data;
      console.log('엑셀 분석 결과:', response.data);
      console.log('시트 수:', response.data.sheets?.length);
      response.data.sheets?.forEach((sheet: any, idx: number) => {
        console.log(`시트 ${idx}: detectedHeader =`, sheet.detectedHeader);
      });
    } else {
      excelError.value = response.data.message || '분석에 실패했습니다.';
    }
  } catch (error: any) {
    console.error('엑셀 분석 오류:', error);
    excelError.value = error.response?.data?.message || '엑셀 파일 분석 중 오류가 발생했습니다.';
  } finally {
    isAnalyzing.value = false;
  }
};

// 엑셀 데이터 처리 (AI 분석 후 등록)
const processExcelData = async () => {
  if (!excelFile.value) return;
  
  const sisulCode = store.state.user?.m_sisul_code;
  if (!sisulCode) {
    excelError.value = '시설 코드를 찾을 수 없습니다.';
    return;
  }
  
  isProcessing.value = true;
  processComplete.value = false;
  processLogs.value = [];
  processStatus.value = { message: '처리 시작...' };
  processResult.value = { incomeCount: 0, expenseCount: 0, errorCount: 0 };
  
  // AbortController 생성
  abortController = new AbortController();
  
  const formData = new FormData();
  formData.append('file', excelFile.value);
  formData.append('sisulCode', sisulCode);
  formData.append('year', props.currentYear);
  formData.append('month', props.currentMonth);
  
  try {
    // SSE를 사용한 실시간 진행 상황 수신
    const response = await fetch('/api/fna/inout/excel/process', {
      method: 'POST',
      body: formData,
      signal: abortController.signal
    });
    
    if (!response.ok) {
      throw new Error('서버 오류가 발생했습니다.');
    }
    
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('스트림을 읽을 수 없습니다.');
    }
    
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || ''; // 마지막 불완전한 이벤트는 버퍼에 유지
      
      for (const event of events) {
        if (event.startsWith('data: ')) {
          try {
            const data = JSON.parse(event.slice(6));
            console.log('[SSE] 이벤트 수신:', data.type, data);
            handleProcessEvent(data);
          } catch (e) {
            console.error('[SSE] JSON 파싱 오류:', e, event);
          }
        }
      }
    }
  } catch (error: any) {
    // 사용자가 중단한 경우
    if (error.name === 'AbortError') {
      console.log('엑셀 처리가 사용자에 의해 중단됨');
      processStatus.value.message = '처리가 중단되었습니다.';
      processLogs.value.push({ type: 'warning', message: '처리가 사용자에 의해 중단되었습니다.' });
      emit('saved'); // 처리된 데이터 새로고침
    } else if (error.message?.includes('network') || error.name === 'TypeError') {
      // 네트워크 에러
      console.error('네트워크 오류:', error);
      processStatus.value.message = '연결이 끊어졌습니다.';
      processLogs.value.push({ type: 'error', message: '서버 연결이 끊어졌습니다. 처리된 데이터는 저장되었을 수 있습니다.' });
      emit('saved');
    } else {
      console.error('엑셀 처리 오류:', error);
      processStatus.value.message = '오류가 발생했습니다.';
      excelError.value = error.message || '처리 중 오류가 발생했습니다.';
    }
    isProcessing.value = false;
  } finally {
    abortController = null;
  }
};

// SSE 이벤트 처리
const handleProcessEvent = (data: any) => {
  switch (data.type) {
    case 'start':
    case 'sheet_start':
    case 'sheet_info':
    case 'sheet_skip':
    case 'analysis_complete':
      processStatus.value.message = data.message;
      processLogs.value.push({ type: 'info', message: data.message });
      break;
    
    // 전년도이월금 관련 이벤트
    case 'carryover_detected':
    case 'carryover_insert':
      processStatus.value.message = data.message;
      processLogs.value.push({ type: 'carryover', message: data.message });
      break;
      
    case 'carryover_complete':
      processStatus.value.message = data.message;
      processLogs.value.push({ type: 'success', message: `✓ ${data.message}` });
      scrollLogToBottom();
      break;
      
    case 'phase_start':
      processStatus.value = {
        message: data.message,
        phase: data.phase,
        current: 0,
        total: data.count
      };
      processLogs.value.push({ type: 'phase', message: data.message });
      break;
      
    case 'item_processing':
      processStatus.value = {
        ...processStatus.value,
        message: data.message,
        current: data.current,
        total: data.total,
        phase: data.phase,
        incomeProcessed: data.incomeProcessed,
        expenseProcessed: data.expenseProcessed,
        currentItem: data.memo ? {
          sheetName: data.sheetName || '',
          rowIndex: data.rowIndex || 0,
          memo: data.memo,
          inout_date: data.inout_date
        } : processStatus.value.currentItem
      };
      break;
      
    case 'item_complete':
      processStatus.value = {
        ...processStatus.value,
        message: data.message,
        current: data.current,
        currentItem: {
          sheetName: processStatus.value.currentItem?.sheetName || '',
          rowIndex: processStatus.value.currentItem?.rowIndex || 0,
          memo: data.memo,
          scode_name: data.scode_name,
          rel_acc_name: data.rel_acc_name
        }
      };
      processLogs.value.push({ type: 'success', message: data.message });
      scrollLogToBottom();
      break;
      
    case 'item_error':
      processLogs.value.push({ type: 'error', message: `오류: ${data.memo} - ${data.error}` });
      scrollLogToBottom();
      break;
      
    case 'phase_complete':
      processStatus.value.message = data.message;
      processLogs.value.push({ type: 'phase', message: data.message });
      break;
      
    case 'complete':
      processComplete.value = true;
      isProcessing.value = false;
      processStatus.value = { message: data.message };
      processResult.value = {
        incomeCount: data.incomeCount,
        expenseCount: data.expenseCount,
        errorCount: data.errorCount
      };
      processLogs.value.push({ type: 'complete', message: data.message });
      emit('saved'); // 데이터 새로고침
      break;
    
    case 'cancelled':
      isProcessing.value = false;
      processStatus.value = { message: data.message };
      processLogs.value.push({ type: 'warning', message: data.message });
      emit('saved'); // 처리된 데이터라도 새로고침
      break;
      
    case 'error':
      excelError.value = data.message;
      isProcessing.value = false;
      processLogs.value.push({ type: 'error', message: data.message });
      break;
  }
};

// 로그 스크롤
const scrollLogToBottom = () => {
  nextTick(() => {
    if (processLogRef.value) {
      processLogRef.value.scrollTop = processLogRef.value.scrollHeight;
    }
  });
};

// 로그 아이콘
const getLogIcon = (type: string): string => {
  switch (type) {
    case 'success': return 'mdi-check';
    case 'error': return 'mdi-alert-circle';
    case 'warning': return 'mdi-alert';
    case 'phase': return 'mdi-flag';
    case 'complete': return 'mdi-check-circle';
    case 'carryover': return 'mdi-bank-transfer';
    default: return 'mdi-information';
  }
};

// 로그 아이콘 색상
const getLogIconColor = (type: string): string => {
  switch (type) {
    case 'success': return '#10b981';
    case 'error': return '#ef4444';
    case 'warning': return '#f59e0b';
    case 'phase': return '#6366f1';
    case 'complete': return '#10b981';
    case 'carryover': return '#f59e0b';
    default: return '#64748b';
  }
};

// 처리 완료 후
const onProcessComplete = () => {
  emit('saved');
  closeDialog();
};

// SSE 연결 중단을 위한 AbortController
let abortController: AbortController | null = null;

// 다이얼로그 닫기
const closeDialog = () => {
  // 처리 중이면 확인
  if (isProcessing.value) {
    if (!confirm('엑셀 처리가 진행 중입니다. 중단하시겠습니까?\n중단하면 현재까지 처리된 데이터만 저장됩니다.')) {
      return;
    }
    // SSE 연결 중단
    cancelExcelProcess();
  }
  dialogOpen.value = false;
  resetForm();
};

// 엑셀 처리 중단
const cancelExcelProcess = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  isProcessing.value = false;
  processStatus.value.message = '처리가 중단되었습니다.';
};

// 폼 초기화
const resetForm = () => {
  const defaultDate = `${props.currentYear}-${props.currentMonth}-01`;
  formData.value = {
    gubun: 'OUT',
    inout_date: defaultDate,
    price: 0,
    memo: ''
  };
  prediction.value = null;
  predictionError.value = '';
  activeTab.value = 'input';
  // 엑셀 상태 초기화
  excelFile.value = null;
  excelAnalysis.value = null;
  excelError.value = '';
  // 엑셀 처리 상태 초기화
  isProcessing.value = false;
  processComplete.value = false;
  processStatus.value = { message: '' };
  processLogs.value = [];
  processResult.value = { incomeCount: 0, expenseCount: 0, errorCount: 0 };
};

// 다이얼로그가 열릴 때 폼 초기화
watch(dialogOpen, (newVal) => {
  if (newVal) {
    resetForm();
  }
});

// 페이지 이탈 시 경고 핸들러
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (isProcessing.value) {
    e.preventDefault();
    e.returnValue = '엑셀 처리가 진행 중입니다. 페이지를 떠나시겠습니까?';
    return e.returnValue;
  }
};

// 컴포넌트 마운트/언마운트 시 이벤트 리스너 관리
onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  // 처리 중이었다면 중단
  if (isProcessing.value && abortController) {
    abortController.abort();
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/pgmInoutAuto.scss";
</style>
